
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import { ColorEnum, PositionEnum } from '../enums'

import { EpbBadge } from './shared/epb-badge'
import { EpbButton } from './shared/epb-button'
import { EpbText } from './shared/epb-text'

export interface Suggestion {
  value: string
  label?: string
}

export default defineComponent({
  name: 'Searchbox',
  components: {
    EpbText,
    EpbBadge,
    EpbButton,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    help: {
      type: String,
    },
    maxLength: {
      type: Number,
    },
    size: {
      type: String, // 'medium' | 'large'
      default: 'medium',
    },
    border: {
      type: Boolean,
      default: false,
    },
    iconPosition: {
      type: String as PropType<PositionEnum>, // PositionEnum.LEFT | PositionEnum.RIGHT
      default: PositionEnum.RIGHT,
    },
    suggestions: {
      type: Array as PropType<Array<Suggestion>>,
      default: () => [],
    },
    loading: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  setup() {
    return {
      ColorEnum,
    }
  },
  data() {
    return {
      baseClass: 'epb-searchbox',
      innerValue: undefined,
      hovered: false,
      focused: false,
      suggestionsVisible: false,
      highlightedSuggestionIndex: -1,
      PositionEnum,
      isHovered: false,
    }
  },
  computed: {
    classes(): string[] {
      return [
        this.baseClass,
        `${this.baseClass}--${this.size}`,
        {
          [`${this.baseClass}--border`]: this.border,
          [`${this.baseClass}--hovered`]: !this.disabled && this.hovered,
          [`${this.baseClass}--focused`]: !this.disabled && this.focused,
          [`${this.baseClass}--disabled`]: this.disabled,
        },
      ]
    },
    clearIcon(): string {
      let icon: string
      if (this.innerValue) {
        icon = 'close'
      } else if (this.iconPosition === PositionEnum.RIGHT) {
        icon = 'search'
      }
      return icon
    },
    helpBadgeSize(): string {
      if (this.size === 'large') {
        return 'small'
      }
      return 'mini'
    },
  },
  watch: {
    value: {
      handler(value: string): void {
        this.innerValue = value
        if (this.focused) {
          this.openSuggestions()
        }
      },
      immediate: true,
    },
    suggestions: {
      handler(): void {
        this.highlightedSuggestionIndex = -1
      },
    },
  },
  mounted(): void {
    document.addEventListener('click', this.onExternalClick)
  },
  beforeDestroy(): void {
    document.removeEventListener('click', this.onExternalClick)
  },
  methods: {
    onFocus(): void {
      this.focused = true
      this.openSuggestions()
    },
    openSuggestions(): void {
      if (this.suggestionsVisible) {
        return
      }
      this.suggestionsVisible = true
      this.$emit('suggestions-open')
    },
    onBlur(): void {
      this.focused = false
    },
    input(value: string): void {
      this.innerValue = value
      this.$emit('input', value)
    },
    onKeyboardEnter(event: KeyboardEvent): void {
      const highlightedSuggestion = this.suggestions[this.highlightedSuggestionIndex]
      if (highlightedSuggestion) {
        this.selectSuggestion(highlightedSuggestion)
      } else {
        this.change((event.target as HTMLInputElement).value)
      }
    },
    selectSuggestion(suggestion: Suggestion): void {
      this.$emit('select', suggestion.value)
      this.closeSuggestions()
    },
    closeSuggestions(): void {
      if (!this.suggestionsVisible) {
        return
      }
      this.suggestionsVisible = false
      this.highlightedSuggestionIndex = -1
      this.$emit('suggestions-close')
    },
    change(value: string): void {
      if (value !== this.value) {
        this.$emit('change', value)
      }
    },
    highlightSuggestion(index: number): void {
      if (!this.suggestions.length) {
        return
      }
      if (index === this.suggestions.length) {
        index = 0
      } else if (index < 0) {
        index = this.suggestions.length - 1
      }
      this.highlightedSuggestionIndex = index
      this.openSuggestions()
    },
    clear(): void {
      this.input('')
      this.change('')
    },
    getSuggestionClasses(suggestion: Suggestion, index: number) {
      const baseClass = `${this.baseClass}__suggestion`
      return [
        baseClass,
        {
          [`${baseClass}--highlighted`]: index === this.highlightedSuggestionIndex,
        },
      ]
    },
    onExternalClick(event: MouseEvent): void {
      if (!this.$el.contains(event.target)) {
        this.closeSuggestions()
      }
    },
    resetInput(): void {
      this.innerValue = this.value
    },
  },
})
